<script>
import { reject } from 'lodash'
import Modal from './Modal.vue'

export default {
    name: 'NewPositionSelection',
    components: {
        Modal,
    },
    props: {
        value: {
            type: [Array, Object],
            default: () => null,
        },
        closeButtonText: {
            type: String,
            default: 'Fermer',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        errorString: {
            type: String,
            default: '',
        },
        formName: {
            type: String,
            default: '',
        },
        initFormInputName: {
            type: String,
            default: 'newPosition',
        },
        positionCategories: {
            type: Array,
            default() {
                return []
            },
        },
        labelText: {
            type: String,
            default: 'Mes postes',
        },
        labelAsPlaceholder: {
            type: Boolean,
            default: false,
        },
        labelClass: {
            type: String,
            default: '',
        },
        modalTitle: {
            type: String,
            default: 'Sélectionner un ou plusieurs postes',
        },
        multiple: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:value'],
    data() {
        return {
            currentPositionCategory: this.positionCategories[0],
            selected: this.value ? this.value : this.multiple ? [] : null,
            // VueJS component id to define a unique form input id in the page
            id: this.$.uid,
        }
    },
    computed: {
        formInputName() {
            if (!this.multiple) {
                return this.initFormInputName
            }

            return this.initFormInputName + '[]'
        },
        fullLabelText() {
            if (this.required) {
                return `${this.labelText} *`
            }

            return this.labelText
        },
        positions() {
            return this.positionCategories.reduce(
                (acc, positionCategory) => acc.concat(positionCategory.new_positions),
                []
            )
        },
        selectedPositions() {
            if (this.multiple) {
                return this.positions.filter(position => this.selected.includes(position.id))
            }

            return [this.positions.find(position => this.selected == position.id)].filter(val => null != val)
        },
    },
    methods: {
        isChecked(newPosition) {
            return this.selected && this.selected.id === newPosition.id
        },
        removeNewPosition(newPosition) {
            if (this.disabled) {
                return
            }

            if (this.multiple) {
                this.selected = reject(this.selected, id => id == newPosition.id)
            } else {
                this.selected = null
            }

            this.selectedChanges()
        },
        selectedChanges() {
            this.$emit('update:value', this.selected)

            this.$nextTick(() => {
                const input = this.$el.querySelector(`[name="${this.formInputName}"]`)
                if (input) input.dispatchEvent(new Event('change', { bubbles: true }))
            })

            if (!this.multiple) {
                this.toggleModal()
            }
        },
        setCurrentPositionCategory(positionCategory) {
            this.currentPositionCategory = positionCategory
        },
        toggleModal() {
            if (!this.disabled) {
                this.$refs.newPositionSelectorModal.toggle()
            }
        },
    },
}
</script>

<template>
    <div class="new-position-selection">
        <!-- element displaying the selected values -->
        <div class="form-group">
            <label v-if="!labelAsPlaceholder">
                <span v-html="fullLabelText"></span>
                <span v-if="!required && labelClass != ''" title="Champ obligatoire" :class="labelClass">*</span>
            </label>
            <div class="input-group">
                <div class="form-control" :class="{ 'is-invalid': errorString.length }" @click.self="toggleModal()">
                    <span
                        v-if="labelAsPlaceholder"
                        v-show="!selectedPositions.length"
                        class="text-muted"
                        v-html="fullLabelText"
                    />
                    <a
                        v-for="newPosition in selectedPositions"
                        :key="newPosition.id"
                        class="badge badge-light"
                        href="#"
                        :title="newPosition.name"
                        @click.prevent="removeNewPosition(newPosition)"
                    >
                        <span class="position-name" v-text="newPosition.name" />
                        <span>&times;</span>
                    </a>
                </div>
                <div class="invalid-feedback" v-text="errorString"></div>
            </div>

            <template v-if="multiple">
                <template v-for="itemId in selected" :key="itemId">
                    <input
                        v-if="formName"
                        :form="formName"
                        type="hidden"
                        :name="formInputName"
                        :value="itemId"
                        :disabled="disabled"
                    />
                    <input v-else type="hidden" :name="formInputName" :value="itemId" :disabled="disabled" />
                </template>
            </template>
            <template v-else>
                <input
                    v-if="formName"
                    :form="formName"
                    type="hidden"
                    :name="formInputName"
                    :value="selected"
                    :disabled="disabled"
                />
                <input v-else type="hidden" :name="formInputName" :value="selected" :disabled="disabled" />
            </template>
        </div>

        <modal v-if="!disabled" ref="newPositionSelectorModal" :size="'large'">
            <template #title>
                <h4 class="text-center" v-html="modalTitle"></h4>
            </template>

            <div class="container">
                <div class="nav nav-tabs btn-group row">
                    <a
                        v-for="positionCategory in positionCategories"
                        :key="positionCategory.id"
                        class="col-12 col-lg-4 btn btn-blue-calypso p-3 p-lg-1 nav-border"
                        :class="{
                            active: currentPositionCategory.id && positionCategory.id == currentPositionCategory.id,
                        }"
                        href="#"
                        @click.prevent="setCurrentPositionCategory(positionCategory)"
                        v-text="positionCategory.name"
                    />
                </div>
                <div class="tab-content">
                    <div class="tab-pane active show">
                        <div
                            v-for="newPosition in currentPositionCategory.new_positions"
                            :key="newPosition.id"
                            class="custom-control"
                            :class="{ 'custom-checkbox': multiple, 'custom-radio': !multiple }"
                        >
                            <input
                                v-if="multiple && formName"
                                :id="'newPositionInput-' + id + newPosition.id"
                                v-model="selected"
                                type="checkbox"
                                class="custom-control-input"
                                :form="formName"
                                :value="newPosition.id"
                                @change="selectedChanges"
                            />
                            <input
                                v-else-if="multiple && !formName"
                                :id="'newPositionInput-' + id + newPosition.id"
                                v-model="selected"
                                type="checkbox"
                                class="custom-control-input"
                                :value="newPosition.id"
                                @change="selectedChanges"
                            />
                            <input
                                v-else-if="!multiple && formName"
                                :id="'newPositionInput-' + id + newPosition.id"
                                v-model="selected"
                                type="radio"
                                class="custom-control-input"
                                :form="formName"
                                :value="newPosition.id"
                                :checked="isChecked(newPosition)"
                                @change="selectedChanges"
                            />
                            <input
                                v-else-if="!multiple && !formName"
                                :id="'newPositionInput-' + id + newPosition.id"
                                v-model="selected"
                                type="radio"
                                class="custom-control-input"
                                :value="newPosition.id"
                                :checked="isChecked(newPosition)"
                                @change="selectedChanges"
                            />
                            <label
                                class="custom-control-label"
                                :for="'newPositionInput-' + id + newPosition.id"
                                v-text="newPosition.name"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <template #footer>
                <div class="text-center text-sm-right">
                    <button
                        type="button"
                        class="btn btn-dugong"
                        @click.prevent="toggleModal()"
                        v-text="closeButtonText"
                    />
                </div>
            </template>
        </modal>
    </div>
</template>

<style scoped lang="scss">
.new-position-selection {
    .nav-tabs {
        border-bottom: 0;

        .nav-border {
            @media (width <= 992px) {
                border: 2px solid white;
                border-radius: 30px;
            }
        }
    }

    .input-group {
        .form-control {
            min-height: 37px;
            height: auto;
            overflow: hidden;

            .badge {
                margin-right: 0.5rem;
                font-weight: 200;
                font-size: 0.9rem;
                padding: 7px;
                margin: 3px;

                .new-position-name ~ span {
                    font-weight: 700;
                }
            }
        }
    }

    .tab-content {
        margin-top: 2rem;

        .custom-control {
            margin: 0.75rem 1rem;
            padding-bottom: 0.5rem;

            border-bottom: 0.75px solid #707070;
        }
    }
}

.custom-control-input:indeterminate {
    & ~ .custom-control-label::before {
        background-color: #fff !important;
    }
}
</style>
